/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import _ from 'lodash';

import { BlockUnits, MemoryUnits, PgConfigValueWithUnit, TimeUnits } from '../schema/pg-config.consts';
import { schema, uiSchema } from '../schema/pg-config.schema';
import { Config } from '../schema/pg-config.types';
import { getUiSchemaOption } from './pg-config.util';

export const deserialize = (data: Record<string, string>, renderedPgConfigSchema: typeof schema): Config => {
  let config: Config = {};
  const findEntry = (properties: any, currentKey?: string): void => {
    Object.entries(properties).forEach(([key, value]: [string, any]) => {
      const concatenatedKey = currentKey ? `${currentKey}.${key}` : key;
      // If it's an object, call this function again
      if (value['type'] === 'object') {
        findEntry(value['properties'], concatenatedKey);
      } else {
        // Retrieve the current setting for this key
        const rawSetting = _.get(data, key);
        const setting: {
          value: string;
          unit?: MemoryUnits | TimeUnits | BlockUnits;
          target?: string;
        } =
          typeof rawSetting === 'object'
            ? (_.get(data, key) as unknown as { value: string })!
            : PgConfigValueWithUnit.test(rawSetting)
              ? {
                  value: (PgConfigValueWithUnit.exec(rawSetting) || [])[1]!,
                  unit: (PgConfigValueWithUnit.exec(rawSetting) || [])[2] as MemoryUnits | TimeUnits | BlockUnits,
                }
              : { value: rawSetting! };

        if (setting.value !== undefined) {
          // Parse the string if necessary
          let parsedValue: any;
          switch (value['type']) {
            case 'integer':
              parsedValue = parseInt(setting.value);
              break;
            case 'number':
              parsedValue = Number(setting.value);
              break;
            case 'boolean':
              parsedValue = ['on', 'true', 'yes', '1'].includes(setting.value.toString().toLowerCase());
              break;
            default:
              parsedValue = setting.value;
          }

          // If they are in a certain unit, convert it to the one found in the UiSchema
          let schemaUnit: string | undefined = undefined;
          if (setting.unit) {
            schemaUnit = getUiSchemaOption(key)?.unit;
            if (setting.unit === BlockUnits.EightKB) {
              setting.unit = MemoryUnits.Kilobytes;
              parsedValue = parsedValue * 8;
            }
            if (schemaUnit && schemaUnit !== setting.unit) {
              if (_.values(MemoryUnits).includes(setting.unit as MemoryUnits)) {
                // Formula: x * 1024 ** n
                // x ... The starting file size
                // n ... How many positions in terms of units the size should be shifted
                //         kB to MB would be 1
                //         GB to kB would be -2
                parsedValue =
                  parsedValue *
                  1024 **
                    (_.values(MemoryUnits).indexOf(setting.unit as MemoryUnits) -
                      _.values(MemoryUnits).indexOf(schemaUnit as MemoryUnits));
              } else if (_.values(TimeUnits).includes(setting.unit as TimeUnits)) {
                // Convert the parsed time to microseconds and then to the target unit
                switch (setting.unit as TimeUnits) {
                  case TimeUnits.Microseconds:
                    break;
                  case TimeUnits.Milliseconds:
                    parsedValue *= 1000;
                    break;
                  case TimeUnits.Seconds:
                    parsedValue *= 1000 * 1000;
                    break;
                  case TimeUnits.Minutes:
                    parsedValue *= 1000 * 1000 * 60;
                    break;
                  case TimeUnits.Hours:
                    parsedValue *= 1000 * 1000 * 60 * 60;
                    break;
                  case TimeUnits.Days:
                    parsedValue *= 1000 * 1000 * 60 * 60 * 24;
                    break;
                }
                switch (schemaUnit as TimeUnits) {
                  case TimeUnits.Microseconds:
                    break;
                  case TimeUnits.Milliseconds:
                    parsedValue /= 1000;
                    break;
                  case TimeUnits.Seconds:
                    parsedValue /= 1000 * 1000;
                    break;
                  case TimeUnits.Minutes:
                    parsedValue /= 1000 * 1000 * 60;
                    break;
                  case TimeUnits.Hours:
                    parsedValue /= 1000 * 1000 * 60 * 60;
                    break;
                  case TimeUnits.Days:
                    parsedValue /= 1000 * 1000 * 60 * 60 * 60;
                    break;
                }
              }
            }
          }
          if (setting.target) {
            const result = setting.target?.match(
              new RegExp(
                `^(-?\\d+)(${[
                  ...Object.values(BlockUnits),
                  ...Object.values(MemoryUnits),
                  ...Object.values(TimeUnits),
                ].join('|')})$`,
              ),
            );
            const [targetValue, targetUnit] = result ? [result[1], result[2]] : [setting.target, undefined];
            if (targetValue !== parsedValue.toString()) {
              if (targetUnit !== schemaUnit) {
                console.log('Received non-matching units while deserializing pg-config', {
                  data,
                  renderedPgConfigSchema,
                  key,
                  targetValue,
                  targetUnit,
                  parsedValue,
                  schemaUnit,
                  setting,
                });
              } else {
                // Dirty manipulation
                let uiSchemaTarget: any = {};
                Object.entries(uiSchema).find(([objectKey, objectValue]) =>
                  key === objectKey
                    ? true
                    : Object.entries(objectValue).find(([objectKey2, objectValue2]) => {
                        if (key === objectKey2) {
                          uiSchemaTarget = objectValue2;
                          return true;
                        }
                        return false;
                      }),
                );
                uiSchemaTarget['ui:options'].actualValue = parsedValue;
                parsedValue = typeof parsedValue === 'number' ? Number(targetValue) : targetValue;
              }
            }
          }
          config = _.set(config, concatenatedKey, parsedValue);
        }
      }
    });
  };
  findEntry(renderedPgConfigSchema.properties);
  return config;
};
